import React, { useState } from 'react';
import { useCompanyContext } from '../../../contexts/Company';
import { useDialogsContext } from '../../../contexts/Dialog';
import { useSnackbarContext } from '../../../contexts/Snackbar';
import { useEventTrackerContext } from '../../../contexts/EventTracker';
import { Scroll } from '../../../utils';
import { addBankAccount } from '../../API';
import { PlaidBankAccount, DialogNames } from '../../../Types';
import startCase from 'lodash.startcase';
import messages from '../../../messages';
import Dialog from '../../BuildingBlocks/Dialogs/DialogStyleTwo/Dialog';
import BankAccounts from './BankAccounts';
import SubmitButton from '../../SubmitButton';

export default function SelectBankAccountDialog({
  accounts,
  bank,
  publicToken,
  onSuccess,
  goBack = () => {},
}: {
  accounts: Array<PlaidBankAccount>;
  bank: string;
  publicToken: string;
  onSuccess: (...args) => any;
  goBack?: (...args) => any;
}) {
  const dialog: any = useDialogsContext();
  const snackbar = useSnackbarContext();
  const { business } = useCompanyContext();
  const { trackEvent } = useEventTrackerContext();
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [loading, setLoading] = useState(false);

  const store = business?.sellerStore
    ? startCase(business.sellerStore)
    : 'your store';

  function handleClose() {
    dialog.close(DialogNames.SelectBankAccount);
    goBack();
  }

  function handleAccountSelect(event: React.ChangeEvent<HTMLInputElement>) {
    setSelectedAccountId(event.target.value);
  }

  async function addNewBank() {
    const selectedAccount = accounts?.find(
      (account) => account.id === selectedAccountId,
    );
    if (!selectedAccount) {
      throw Error("Couldn't find selected account");
    }
    try {
      setLoading(true);
      const newBankAccount = await addBankAccount({
        bank,
        plaidPublicToken: publicToken,
        accountId: selectedAccount.id,
        account: selectedAccount.name,
        accountType: selectedAccount.subtype,
        mask: selectedAccount.mask,
      });
      onSuccess(newBankAccount);
      trackEvent({
        event: 'bank_connected',
        institution: bank,
        bank_account: selectedAccount,
        bank_account_number: newBankAccount.accountNumber,
      });
      dialog.close(DialogNames.SelectBankAccount);
      snackbar.show(messages.bank.addSuccess, 'success');
    } catch (error) {
      snackbar.show(messages.bank.errors.addBank, 'error');
    }
    setLoading(false);
  }

  return (
    <Dialog
      header="Select Bank Account"
      description={`Select the account into which sales from ${store} are deposited.`}
      maxWidth={500}
      open={dialog[DialogNames.SelectBankAccount]}
      onClose={handleClose}
      onSubmit={addNewBank}
      scroll={Scroll.paper}
      actions={
        <SubmitButton
          text="Ok"
          disabled={!selectedAccountId}
          isSubmitting={loading}
        />
      }
    >
      <BankAccounts
        accounts={accounts}
        disabled={loading}
        selectedAccountId={selectedAccountId}
        handleAccountSelect={handleAccountSelect}
      />
    </Dialog>
  );
}
